import { CheckCircle2, ShieldAlert, HelpCircle, RotateCcw, Share2, ExternalLink, AlertTriangle, Download, Zap } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import CircularGauge from "@/components/CircularGauge";
import { downloadShareCard } from "@/lib/shareCard";

const VERDICT_META = {
  IA: {
    label: "Hecho con IA",
    sub: "Detectamos señales claras de generación o manipulación por IA.",
    color: "#ef4444",
    bg: "vx-glow-fake",
    Icon: ShieldAlert,
    meterLabel: "% generado por IA",
  },
  REAL: {
    label: "Video real",
    sub: "No detectamos señales significativas de generación por IA.",
    color: "#22c55e",
    bg: "vx-glow-real",
    Icon: CheckCircle2,
    meterLabel: "% probabilidad real",
  },
  INCIERTO: {
    label: "Resultado incierto",
    sub: "La evidencia no es suficiente para una conclusión definitiva.",
    color: "#f59e0b",
    bg: "vx-glow-uncertain",
    Icon: HelpCircle,
    meterLabel: "% confianza",
  },
};

const SEVERITY_STYLE = {
  alta: { color: "#ef4444", label: "ALTA" },
  media: { color: "#f59e0b", label: "MEDIA" },
  baja: { color: "#a1a1aa", label: "BAJA" },
};

export default function ResultCard({ result, onReset, compact = false }) {
  const meta = VERDICT_META[result.verdict] || VERDICT_META.INCIERTO;
  const { Icon } = meta;
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadShareCard(result);
      toast.success("Imagen descargada");
    } catch (e) {
      console.error(e);
      toast.error("No se pudo generar la imagen");
    } finally {
      setDownloading(false);
    }
  };

  const handleShare = async () => {
    const text = `IF Detector: ${meta.label} · ${result.confidence}% · ${result.summary || ""}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Análisis IF Detector", text });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success("Resultado copiado al portapapeles");
      }
    } catch {
      /* user cancelled */
    }
  };

  return (
    <div className="space-y-4" data-testid="result-card">
      {/* Hero verdict card */}
      <div
        className={`vx-card rounded-3xl overflow-hidden relative ${meta.bg} vx-fade-up`}
        data-testid="verdict-hero"
      >
        {/* Source */}
        {(result.source_platform || result.is_cached) && (
          <div className="flex items-center justify-between px-5 sm:px-7 pt-5 gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              {result.source_platform && (
                <div className="inline-flex items-center gap-2 text-xs text-[--vx-text-dim] font-mono uppercase tracking-wider">
                  <span className="w-1.5 h-1.5 rounded-full bg-white/40" />
                  {result.source_platform}
                </div>
              )}
              {result.is_cached && (
                <div
                  className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-mono px-2 py-1 rounded-full bg-[--vx-real]/10 text-[--vx-real] border border-[--vx-real]/30"
                  data-testid="cached-badge"
                  title="Este video ya fue analizado previamente — resultado instantáneo"
                >
                  <Zap className="w-3 h-3" strokeWidth={2.5} />
                  Instantáneo
                </div>
              )}
            </div>
            {result.source_url && (
              <a
                href={result.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-[--vx-text-faint] hover:text-white inline-flex items-center gap-1 font-mono"
              >
                Ver fuente <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 md:gap-10 items-center p-6 sm:p-8 md:p-12">
          {/* Gauge */}
          <div className="flex justify-center md:justify-start">
            <CircularGauge
              value={result.confidence}
              color={meta.color}
              label={meta.meterLabel}
            />
          </div>

          {/* Text */}
          <div className="text-center md:text-left">
            <div
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs font-mono uppercase tracking-wider mb-4"
              style={{
                borderColor: meta.color,
                color: meta.color,
                background: `${meta.color}15`,
              }}
              data-testid="verdict-badge"
            >
              <Icon className="w-3.5 h-3.5" strokeWidth={2.5} />
              Veredicto
            </div>
            <h2
              className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95]"
              style={{ color: meta.color, textShadow: `0 0 40px ${meta.color}55` }}
              data-testid="verdict-label"
            >
              {meta.label}
            </h2>
            <p className="text-base sm:text-lg text-[--vx-text-dim] mt-4 max-w-xl">
              {meta.sub}
            </p>
            {result.summary && (
              <p
                className="text-sm sm:text-base text-white mt-4 leading-relaxed max-w-xl"
                data-testid="verdict-summary"
              >
                {result.summary}
              </p>
            )}

            {result.source_title && (
              <div className="mt-5 inline-block text-xs text-[--vx-text-faint] font-mono max-w-full truncate">
                <span className="text-[--vx-text-dim]">Título: </span>
                {result.source_title}
              </div>
            )}

            <div className="mt-6 flex gap-2 justify-center md:justify-start flex-wrap">
              <Button
                onClick={onReset}
                data-testid="reset-btn"
                className="rounded-xl bg-white text-black hover:bg-white/90 font-semibold"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Analizar otro
              </Button>
              <Button
                onClick={handleDownload}
                disabled={downloading}
                data-testid="download-image-btn"
                className="rounded-xl font-semibold disabled:opacity-60"
                style={{
                  background: meta.color,
                  color: "#0a0a0a",
                  boxShadow: `0 8px 32px ${meta.color}40`,
                }}
              >
                {downloading ? (
                  <>
                    <span className="w-4 h-4 rounded-full border-2 border-black/40 border-t-black vx-spin mr-2" />
                    Generando…
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" strokeWidth={2.5} />
                    Descargar imagen
                  </>
                )}
              </Button>
              <Button
                onClick={handleShare}
                data-testid="share-btn"
                variant="outline"
                className="rounded-xl border-[--vx-border-strong] bg-transparent hover:bg-[--vx-surface-2] text-white"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Compartir
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Reasoning */}
      {result.reasoning && !compact && (
        <div className="vx-card rounded-2xl p-6 sm:p-8 vx-fade-up vx-delay-1" data-testid="reasoning-block">
          <div className="text-xs uppercase tracking-[0.2em] text-[--vx-text-dim] font-mono mb-3">
            Análisis detallado
          </div>
          <p
            className="text-sm sm:text-base leading-relaxed text-white/90 whitespace-pre-line"
            data-testid="reasoning-text"
          >
            {result.reasoning}
          </p>
        </div>
      )}

      {/* Artifacts */}
      {result.artifacts && result.artifacts.length > 0 && (
        <div className="vx-card rounded-2xl overflow-hidden vx-fade-up vx-delay-2">
          <div className="px-6 py-4 border-b border-[--vx-border] flex items-center justify-between">
            <div className="text-xs uppercase tracking-[0.2em] text-[--vx-text-dim] font-mono">
              Indicios detectados
            </div>
            <span className="text-xs font-mono text-[--vx-text-faint]">
              {result.artifacts.length}
            </span>
          </div>
          <div className="divide-y divide-[--vx-border]">
            {result.artifacts.map((a, idx) => {
              const sev = SEVERITY_STYLE[a.severity] || SEVERITY_STYLE.media;
              return (
                <div
                  key={idx}
                  className="p-5 sm:p-6 flex items-start gap-4"
                  data-testid={`artifact-${idx}`}
                >
                  <div
                    className="shrink-0 w-9 h-9 rounded-full grid place-items-center"
                    style={{
                      background: `${sev.color}15`,
                      border: `1px solid ${sev.color}55`,
                    }}
                  >
                    <AlertTriangle
                      className="w-4 h-4"
                      style={{ color: sev.color }}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-display font-semibold text-base sm:text-lg text-white">
                        {a.title}
                      </h4>
                      <span
                        className="text-[10px] font-mono px-2 py-0.5 rounded-full"
                        style={{
                          color: sev.color,
                          background: `${sev.color}15`,
                          border: `1px solid ${sev.color}40`,
                        }}
                      >
                        {sev.label}
                      </span>
                    </div>
                    <p className="text-sm text-[--vx-text-dim] mt-1.5 leading-relaxed">
                      {a.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Disclaimer */}
      <p className="text-[10px] sm:text-xs text-[--vx-text-faint] text-center font-mono">
        Análisis asistido por IA · resultado orientativo · no constituye prueba pericial
      </p>
    </div>
  );
}
